import "./excavationPermitButtons.css";
import React, { useRef, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { ArrowRightLeft } from "lucide-react";
import API_URL from "../Config";
import { normalizeReviewDepartment } from "./departmentReviewActions";
import "./excavationDepartmentReviewList.css";

export const isDepartmentReassignAdmin = () => ["Admin", "Super Admin", "admin"].includes(localStorage.getItem("user_role"));

// Resolve from the full approval list: admins need not be configured reviewers.
export function getReassignmentOptions(approvals, configured, source) {
  const approval = approvals.find(item => source.department_id != null
    ? String(item.department_id) === String(source.department_id)
    : normalizeReviewDepartment(item.department_name) === normalizeReviewDepartment(source.department_name));
  if (!approval?.department_id || approval.approval_status !== "Pending") {
    throw new Error("This approval is no longer pending or has been reassigned. Refresh the department review list.");
  }
  const assigned = new Set(approvals.map(item => String(item.department_id)));
  const seen = new Set();
  const targets = configured.filter(item => {
    const id = String(item.department_id || "");
    if (!id || assigned.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return { approval, targets };
}

export default function DepartmentReassignAction({ permitId, permitLabel, departmentId, departmentName, approvalStatus, disabled = false, hasUnsavedChanges = false, onSuccess, onBusyChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState(null);
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const loadVersion = useRef(0);
  if (!isDepartmentReassignAdmin() || (approvalStatus && approvalStatus !== "Pending")) return null;

  const loadOptions = async () => {
    const headers = { Authorization: localStorage.getItem("authKey") };
    const [permitResponse, approvalResponse] = await Promise.all([
      axios.get(`${API_URL}/api/get_permit_work_details/${permitId}`, { headers }),
      axios.get(`${API_URL}/api/permit/department/approvals/${permitId}`, { headers }),
    ]);
    const permitTypeId = permitResponse.data?.permit_details?.[0]?.permit_type_id;
    if (!permitTypeId) throw new Error("The permit type could not be loaded. Please retry.");
    const setup = await axios.get(`${API_URL}/api/permit/departments/setup/${permitTypeId}`, { headers });
    return getReassignmentOptions(approvalResponse.data?.department_approvals || [], setup.data?.departments || [], {
      department_id: departmentId, department_name: departmentName,
    });
  };

  const load = async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setError("");
    setOptions(null);
    try {
      const result = await loadOptions();
      if (version === loadVersion.current) setOptions(result);
    }
    catch (err) {
      if (version === loadVersion.current) setError(err.response?.data?.message || err.message || "Unable to load departments. Please retry.");
    }
    finally { if (version === loadVersion.current) setLoading(false); }
  };
  const begin = () => {
    if (busy.current) return;
    setTargetId(""); setReason(""); setOpen(true);
    onBusyChange?.(true);
    load();
  };
  const close = () => {
    if (busy.current) return;
    loadVersion.current += 1;
    setOpen(false);
    onBusyChange?.(false);
  };
  const submit = async event => {
    event.preventDefault();
    if (busy.current || !isDepartmentReassignAdmin() || !targetId || !reason.trim() || !options) return;
    busy.current = true;
    setSubmitting(true); setError("");
    let response;
    try {
      const latest = await loadOptions();
      setOptions(latest);
      const target = latest.targets.find(item => String(item.department_id) === targetId);
      if (!target) {
        setTargetId("");
        throw new Error("The selected department is no longer available. Select another department.");
      }
      response = await axios.post(`${API_URL}/api/permit/department/reassign`, {
        permit_id: permitId,
        from_department_id: latest.approval.department_id,
        to_department_id: target.department_id,
        reason: reason.trim(),
      }, { headers: { Authorization: localStorage.getItem("authKey"), "Content-Type": "application/json" } });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to reassign the approval. Please retry.");
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
    if (response) {
      setOpen(false);
      onBusyChange?.(false);
      await onSuccess?.(response.data?.message || "Approval reassigned successfully");
    }
  };

  return <>
    <Button variant="outlined" size="small" startIcon={<ArrowRightLeft size={15} />} disabled={disabled || open}
      onClick={begin} aria-label={`Reassign ${permitLabel || permitId} ${departmentName || "department"} review`}
      sx={{ textTransform: "none", whiteSpace: "nowrap", borderRadius: 2 }}>Reassign</Button>
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="department-reassign-title"
      PaperProps={{ className: "excavation-review-list-dialog", component: "form", onSubmit: submit }}>
      <DialogTitle id="department-reassign-title"><ArrowRightLeft size={22} aria-hidden="true" />Reassign department review</DialogTitle>
      <DialogContent>
        <Typography className="excavation-list-dialog-context">{permitLabel || permitId} · {departmentName}</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>Transfer this pending approval to another configured department. The reason will be recorded in the workflow history.</Alert>
        {hasUnsavedChanges && <Alert severity="warning" sx={{ mb: 2 }}>You have unsaved review details. Confirming reassignment will discard these edits and return you to the department review list.</Alert>}
        {loading && <Box role="status" sx={{ display: "flex", gap: 2, alignItems: "center", py: 2 }}><CircularProgress size={22} />Loading available departments…</Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }} action={!options && !submitting ? <Button onClick={load} color="inherit">Retry</Button> : undefined}>{error}</Alert>}
        {options && <>
          {!options.targets.length && <Alert severity="warning" sx={{ mb: 2 }}>No other configured departments are available. Departments that already have an approval cannot be selected.</Alert>}
          <TextField label="Current department" value={options.approval.department_name || departmentName || ""} fullWidth margin="normal" slotProps={{ input: { readOnly: true } }} />
          <TextField select required label="Reassign to department" fullWidth margin="normal" value={targetId} onChange={event => setTargetId(event.target.value)} disabled={submitting || !options.targets.length}
            helperText="Only configured departments without an existing approval are available.">
            {options.targets.map(item => <MenuItem key={item.department_id} value={String(item.department_id)}>{item.department_name}</MenuItem>)}
          </TextField>
          <TextField required label="Reason for reassignment" fullWidth multiline minRows={3} margin="normal" value={reason} onChange={event => setReason(event.target.value)} disabled={submitting} placeholder="Explain why this review needs another department" />
        </>}
      </DialogContent>
      <DialogActions>
        <Button className="excavation-permit-button" variant="outlined" onClick={close} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={loading || submitting || !options?.targets.some(item => String(item.department_id) === targetId) || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ArrowRightLeft size={16} />}>{submitting ? "Reassigning…" : "Confirm reassignment"}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
